import React, { memo } from "react";
import { useToggle } from "react-use";

import { Tooltip as MuiTooltip, TooltipProps } from "@mui/material";

type PropTypes = TooltipProps;

type ClonedComponentPropTypes = {
  onFocus: () => void;
  onBlur: () => void;
  children: React.ReactElement;
};

const ClonedComponent: React.FC<ClonedComponentPropTypes> = ({
  children,
  onFocus,
  onBlur,
}) => {
  return React.cloneElement(children, {
    onFocus,
    onBlur,
  });
};

const MemoisedClonedComponent = memo(
  ClonedComponent,
  (prevProps, nextProps) => {
    return prevProps.children.type === nextProps.children.type;
  },
);

export const TooltipOnFocus: React.FC<PropTypes> = ({ children, ...props }) => {
  const [open, toggle] = useToggle(false);

  const handleFocus = () => {
    toggle(true);
  };

  const handleBlur = () => {
    toggle(false);
  };

  const component = (
    <MemoisedClonedComponent onFocus={handleFocus} onBlur={handleBlur}>
      {children}
    </MemoisedClonedComponent>
  );

  return (
    <MuiTooltip
      arrow
      open={open}
      onClose={() => toggle(false)}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      {...props}
    >
      <div>{component}</div>
    </MuiTooltip>
  );
};
